import { useState, useCallback, useEffect } from 'react';
import { FAVOURITE_VOICES_LIMIT } from '@src/constants/voiceLibrary';

const useHandleFavouriteVoices = ({ favouriteVoiceCodes }) => {
  const [globalPendingFavorites, setGlobalPendingFavorites] = useState(
    new Set(),
  );
  const [
    queuePostProcessingFavouriteVoices,
    setQueuePostProcessingFavouriteVoices,
  ] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [
    globalCurrentFavouriteVoiceCodes,
    setGlobalCurrentFavouriteVoiceCodes,
  ] = useState([]);

  const canAddToFavorites = useCallback(
    (addVoiceCode, pendingSet) => {
      // Count current favorites
      const currentCount = favouriteVoiceCodes.length;

      // Count pending additions (excluding the current voice if it's being removed)
      const pendingAdditions = Array.from(pendingSet).filter(
        (code) => code !== addVoiceCode && !favouriteVoiceCodes.includes(code),
      ).length;

      // Check if current voice would be an addition
      const wouldBeAddition = !favouriteVoiceCodes.includes(addVoiceCode);

      const totalAfterPending =
        currentCount + pendingAdditions + (wouldBeAddition ? 1 : 0);

      return totalAfterPending <= FAVOURITE_VOICES_LIMIT;
    },
    [favouriteVoiceCodes],
  );

  // Function to handle favorite toggle request
  const handleFavoriteToggleRequest = useCallback(
    (addVoiceCode) => {
      // Check if this voice is already being processed
      if (globalPendingFavorites.has(addVoiceCode)) {
        return { allowed: false };
      }

      const isCurrentlyFavorite = favouriteVoiceCodes.includes(addVoiceCode);

      // If adding to favorites, check the limit
      if (
        !isCurrentlyFavorite &&
        !canAddToFavorites(addVoiceCode, globalPendingFavorites)
      ) {
        return { allowed: false, reason: 'limit' };
      }

      // Add to pending set
      setGlobalPendingFavorites((prev) => new Set([...prev, addVoiceCode]));
      return { allowed: true };
    },
    [globalPendingFavorites, favouriteVoiceCodes, canAddToFavorites],
  );

  // Function to handle favorite processing completion
  const handleFavoriteProcessingComplete = useCallback(
    (voice) => {
      const voiceCode = voice.code;
      setGlobalPendingFavorites((prev) => {
        const newSet = new Set(prev);
        newSet.delete(voiceCode);
        return newSet;
      });

      const favouriteVoiceCodeExisted =
        globalCurrentFavouriteVoiceCodes.includes(voiceCode);
      if (favouriteVoiceCodeExisted) {
        setGlobalCurrentFavouriteVoiceCodes((prev) =>
          prev.filter((code) => code !== voiceCode),
        );
      } else {
        setGlobalCurrentFavouriteVoiceCodes((prev) => [...prev, voiceCode]);
      }
    },
    [globalCurrentFavouriteVoiceCodes],
  );

  const addToQueuePostProcessingFavouriteVoices = ({ voice }) => {
    setQueuePostProcessingFavouriteVoices([
      ...queuePostProcessingFavouriteVoices,
      voice,
    ]);
  };

  const processingQueueFavouriteVoices = async () => {
    if (queuePostProcessingFavouriteVoices.length === 0 || isProcessing) return;
    setIsProcessing(true);
    const task = queuePostProcessingFavouriteVoices[0];
    handleFavoriteProcessingComplete(task);
    setQueuePostProcessingFavouriteVoices((prev) => prev.slice(1));
    setIsProcessing(false);
  };

  const handleErrorProcessingFavouriteVoices = (voiceCode) => {
    setGlobalPendingFavorites((prev) => {
      const newSet = new Set(prev);
      newSet.delete(voiceCode);
      return newSet;
    });
  };

  useEffect(() => {
    processingQueueFavouriteVoices();
  }, [isProcessing, queuePostProcessingFavouriteVoices]);

  return {
    globalPendingFavorites,
    setGlobalPendingFavorites,
    globalCurrentFavouriteVoiceCodes,
    setGlobalCurrentFavouriteVoiceCodes,
    handleFavoriteToggleRequest,
    handleFavoriteProcessingComplete,
    addToQueuePostProcessingFavouriteVoices,
    handleErrorProcessingFavouriteVoices,
  };
};

export default useHandleFavouriteVoices;
